import { FC, Suspense, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router';

import { BodyLoading } from '@cvt/components/layout/BodyLoading';
import ErrorBoundary from '@cvt/components/ErrorBoundary';

import { useEnrollment } from '../hooks/useEnrollment';
import { useEnrollmentCrud } from '../hooks/useEnrollmentCrud';
import { ProgramWelcome } from '../partials/ProgramWelcome';
import { ProgramStepFlow } from '../partials/ProgramStepFlow';
import { ProgramCompletion } from '../partials/ProgramCompletion';
import { JourneyReviewPartial } from '../partials/JourneyReviewPartial';
import { useEntryToday } from '../hooks/useEntryToday';


const JourneyWelcome: FC<{ enrollmentId: string }> = ({ enrollmentId }) => {
  const { enrollment } = useEnrollment(enrollmentId);
  const navigate = useNavigate();

  const completedStepIds = useMemo(
    () => new Set(enrollment.stepEntries.map(e => e.programStep)),
    [enrollment.stepEntries],
  );

  const isFullyCompleted = useMemo(
    () => enrollment.program.steps.length > 0 &&
      enrollment.program.steps.every(s => completedStepIds.has(s.id)),
    [enrollment.program.steps, completedStepIds],
  );

  const { hasEntryToday } = useEntryToday(enrollment);

  const handleBegin = useCallback(() => {
    if (isFullyCompleted) {
      navigate(`/programs/journey/${enrollmentId}/review`, { replace: true });
    } else if (hasEntryToday) {
      navigate(`/programs/journey/${enrollmentId}/complete`, { replace: true });
    } else {
      navigate(`/programs/journey/${enrollmentId}/step`, { replace: true });
    }
  }, [isFullyCompleted, hasEntryToday, enrollmentId, navigate]);

  return (
    <ProgramWelcome
      program={enrollment.program}
      onBegin={handleBegin}
      ctaLabel={isFullyCompleted ? 'View Activations' : undefined}
    />
  );
};

const JourneyStep: FC<{ enrollmentId: string }> = ({ enrollmentId }) => {
  const { enrollment, refetch } = useEnrollment(enrollmentId);
  const navigate = useNavigate();
  const { createEnrollment } = useEnrollmentCrud();

  const completedStepIds = useMemo(
    () => new Set(enrollment.stepEntries.map(e => e.programStep)),
    [enrollment.stepEntries],
  );

  const orderedSteps = useMemo(() => {
    if (enrollment.stepOrder) {
      const stepsById: Record<number, Programs.ProgramStep> = {};
      enrollment.program.steps.forEach(s => { stepsById[s.id] = s; });
      return enrollment.stepOrder.map(id => stepsById[id]).filter(Boolean);
    }
    return [...enrollment.program.steps].sort((a, b) => a.sequence - b.sequence);
  }, [enrollment.program.steps, enrollment.stepOrder]);

  const nextStep = useMemo(
    () => orderedSteps.find(step => !completedStepIds.has(step.id)),
    [orderedSteps, completedStepIds],
  );

  const isLastStep = useMemo(
    () => nextStep && orderedSteps.filter(step => !completedStepIds.has(step.id)).length === 1,
    [orderedSteps, completedStepIds, nextStep],
  );

  // Check same-day restriction
  const { hasEntryToday } = useEntryToday(enrollment);

  const handleComplete = useCallback(async () => {
    await refetch();

    if (isLastStep) {
      try {
        await createEnrollment.mutateAsync({
          program: enrollment.program.id,
        });
      } catch (error) {
        console.error(error);
      }
    }

    navigate(`/programs/journey/${enrollmentId}/complete`, { replace: true });
  }, [refetch, enrollmentId, navigate, isLastStep, createEnrollment, enrollment.program.id]);

  if (!nextStep) {
    return <Navigate to={`/programs/journey/${enrollmentId}/review`} replace/>;
  }

  if (hasEntryToday) {
    return <Navigate to={`/programs/journey/${enrollmentId}/complete`} replace/>;
  }

  return (
    <ProgramStepFlow
      program={enrollment.program}
      step={nextStep}
      onComplete={handleComplete}
    />
  );
};

const JourneyToday: FC<{ enrollmentId: string }> = ({ enrollmentId }) => {
  const { enrollment, refetch } = useEnrollment(enrollmentId);
  const navigate = useNavigate();

  const { entryToday } = useEntryToday(enrollment);

  const handleComplete = useCallback(async () => {
    await refetch();
    navigate(`/programs/journey/${enrollmentId}/complete`, { replace: true });
  }, [refetch, enrollmentId, navigate]);

  const currentStep = useMemo(() => {
    return enrollment.program.steps.find((step) => {
      return entryToday?.programStep === step.id;
    });
  }, [enrollment.program.steps, entryToday]);

  if (!(entryToday && currentStep)) {
    return <Navigate to={`/programs/journey/${enrollmentId}/complete`} replace/>;
  }

  return (
    <ProgramStepFlow
      program={enrollment.program}
      step={currentStep}
      initialPhase="activation"
      entryToday={entryToday}
      onComplete={handleComplete}
    />
  );
};

const JourneyComplete: FC<{ enrollmentId: string }> = ({ enrollmentId }) => {
  const { enrollment } = useEnrollment(enrollmentId);
  const navigate = useNavigate();

  const lastStepEntry = useMemo(
    () => enrollment.stepEntries.length > 0
      ? enrollment.stepEntries[enrollment.stepEntries.length - 1]
      : undefined,
    [enrollment.stepEntries],
  );

  const handleViewJourney = useCallback(() => {
    navigate(`/programs/journey/${enrollmentId}/review`, { replace: true });
  }, [enrollmentId, navigate]);

  return (
    <ProgramCompletion
      enrolmentId={enrollment.id}
      program={enrollment.program}
      lastStepEntry={lastStepEntry}
      onViewJourney={handleViewJourney}
    />
  );
};

const JourneyReview: FC<{ enrollmentId: string }> = ({ enrollmentId }) => {
  const { enrollment } = useEnrollment(enrollmentId);
  return <JourneyReviewPartial enrollment={enrollment}/>;
};


export const ProgramJourney: FC<{ phase: 'welcome' | 'step' | 'complete' | 'review' | 'today' }> = ({ phase }) => {
  const { enrollmentId } = useParams<{ enrollmentId: string }>();

  if (!enrollmentId) return null;

  const content = {
    welcome: <JourneyWelcome enrollmentId={enrollmentId}/>,
    step: <JourneyStep enrollmentId={enrollmentId}/>,
    complete: <JourneyComplete enrollmentId={enrollmentId}/>,
    review: <JourneyReview enrollmentId={enrollmentId}/>,
    today: <JourneyToday enrollmentId={enrollmentId}/>,
  };

  return (
    <ErrorBoundary>
      <Suspense fallback={<BodyLoading height="60vh"/>}>
        {content[phase]}
      </Suspense>
    </ErrorBoundary>
  );
};
